import { Outlet, useLocation } from "react-router";
import { AppSidebar } from "@/components/app-sidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Calendar, Lock, Moon, Sun } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";
// import { useLogout } from '@/features/auth/hooks/useAuth';
import { useAuthStore } from "@/features/auth/stores/authStore";
import { Button } from "@/components/ui/button";
import { useTheme } from "next-themes";
import { SessionExpiryDialog } from "./SessionExpiryDialog";
import { useIdleTimeout } from "@/shared/hooks/useIdleTimeout";
import { NotificationBell } from "@/features/notifications/components/NotificationBell";
import { RealtimeStatusBadge } from "./RealtimeStatusBadge";
import { useCurrentFinancialYear } from "@/features/accounting/hooks/useFinancialYears";
import { FINANCIAL_YEAR_STATUS_LABEL, FinancialYearStatus } from "@/features/accounting/services/financialYearsApi";

const PAGE_TITLES: Array<{ match: string; title: string }> = [
  { match: "/production/greenhouse-layout", title: "Greenhouse Layout" },
  { match: "/production/greenhouse-stock-take", title: "Greenhouse Stock-Take" },
  { match: "/inventory/stock-recepit", title: "Receive Stock" },
  { match: "/customer-groups", title: "Customer Groups" },
  { match: "/greenhouses", title: "Greenhouses" },
  { match: "/dashboard", title: "Dashboard" },
  { match: "/orders", title: "Orders" },
  { match: "/products", title: "Products" },
  { match: "/admin", title: "Admin" },
  { match: "/customers", title: "Customers" },
  { match: "/suppliers", title: "Suppliers" },
  { match: "/inputs", title: "Inputs" },
  { match: "/batches", title: "Batches" },
  { match: "/reports", title: "Reports" },
  { match: "/payments", title: "Payments" },
  { match: "/invoicing", title: "Invoicing" },
  { match: "/production/reserved-orders", title: "Reserved Stock Orders" },
  { match: "/dispatch/compensation-notes", title: "Compensation Notes" },
  { match: "/admin/drivers", title: "Drivers" },
  { match: "/admin/vehicles", title: "Vehicles" },
  { match: "/admin/transport-rates", title: "Transport Rates" },
  { match: "/dispatch", title: "Dispatch" },
  { match: "/notifications", title: "Notifications" },
];

const getPageTitle = (path: string) =>
  PAGE_TITLES.find((entry) => path.includes(entry.match))?.title ?? "Page";

const FY_STATUS_BADGE_CLASS: Record<FinancialYearStatus, string> = {
  [FinancialYearStatus.PendingApproval]: "bg-orange-100 text-orange-800 border-orange-300",
  [FinancialYearStatus.Open]: "bg-green-100 text-green-800 border-green-300",
  [FinancialYearStatus.Closing]: "bg-amber-100 text-amber-800 border-amber-300",
  [FinancialYearStatus.Closed]: "bg-blue-100 text-blue-800 border-blue-300",
  [FinancialYearStatus.Locked]: "bg-purple-100 text-purple-800 border-purple-300",
};

const formatUserDisplayName = (user: { firstName?: string; otherNames?: string; email?: string }): string => {
  const first = user.firstName?.trim();
  const other = user.otherNames?.trim();
  if (first || other) return [first, other].filter(Boolean).join(" ");
  return user.email ?? "";
};

export const ProtectedLayout = () => {
  const location = useLocation();
  const { logout, user, hasPermission } = useAuthStore();
  const { theme, setTheme } = useTheme();
  // Only fetch the current FY for users who can see FYs — otherwise the API returns 403
  // and we'd noisily 4xx every page load for non-finance users.
  const canViewFY = hasPermission("financial_years.view");
  const { data: currentFy } = useCurrentFinancialYear(canViewFY);

  // Inactivity logout: warns near the idle limit and forces logout when it passes.
  const idle = useIdleTimeout();

  const handleLogout = () => {
    logout();
  };

  const userDisplayName = user ? formatUserDisplayName(user) : "";
  const userPrimaryRole = user?.roles?.[0];

  return (
    <SidebarProvider className="h-dvh">
      <AppSidebar />
      <SidebarInset className="min-h-0">
        <header className="flex h-16 shrink-0 items-center justify-between gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12 px-4">
          <div className="flex items-center gap-2">
            <SidebarTrigger className="-ml-1" />
            <Separator
              orientation="vertical"
              className="mr-2 data-[orientation=vertical]:h-4"
            />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbPage>{getPageTitle(location.pathname)}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>

          <div className="flex items-center gap-1 sm:gap-4">
            <RealtimeStatusBadge />
            <NotificationBell />
            <Button
              variant="ghost"
              size="icon"
              className="hidden xs:inline-flex sm:inline-flex"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            >
              {theme === "dark" ? (
                <Sun className="w-4 h-4" />
              ) : (
                <Moon className="w-4 h-4" />
              )}
            </Button>
            <div className="hidden items-center gap-2 text-sm md:flex">
              <span className="max-w-[160px] truncate text-gray-700 dark:text-gray-200">{userDisplayName}</span>
              {userPrimaryRole && (
                <span className="text-xs text-muted-foreground">({userPrimaryRole})</span>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              // disabled={logoutMutation.isPending}
            >
              {/* {logoutMutation.isPending ? 'Logging out...' : 'Logout'} */}
              Logout
            </Button>
          </div>
        </header>

        <div className="flex flex-1 flex-col gap-4 p-4 pt-0 overflow-y-auto overflow-x-hidden min-h-0">
          <Outlet />
        </div>

        <footer className="flex flex-col gap-1 border-t px-4 py-2 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <Calendar className="size-3.5" />
            {currentFy ? (
              <>
                <span>Current financial year:</span>
                <span className="font-medium text-foreground">{currentFy.label}</span>
                <Badge variant="outline" className={FY_STATUS_BADGE_CLASS[currentFy.status]}>
                  {currentFy.status === FinancialYearStatus.Locked && <Lock className="size-3 mr-1" />}
                  {FINANCIAL_YEAR_STATUS_LABEL[currentFy.status]}
                </Badge>
              </>
            ) : canViewFY ? (
              <span>No current financial year — ask Finance to create one.</span>
            ) : (
              <span>FarmSmart ERP</span>
            )}
          </div>
          {user && (
            <div>
              Logged in as <span className="font-medium text-foreground">{userDisplayName}</span>
              {userPrimaryRole && <> ({userPrimaryRole})</>}
            </div>
          )}
        </footer>
      </SidebarInset>
      <SessionExpiryDialog
        open={idle.showWarning}
        secondsLeft={idle.secondsLeft}
        isRefreshing={idle.isRefreshing}
        onStay={idle.stayLoggedIn}
        onLogout={idle.logoutNow}
      />
    </SidebarProvider>
  );
};
